import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter } from 'lucide-react';

/**
 * Landing Page Footer
 * 
 * Matches the design with solid dark background
 */
export default function LandingFooter() {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update visibility based on whether element is in viewport
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, []);

  const navLinks = [
    { label: 'ACCOMMODATION', to: '/accommodation' },
    { label: 'BAR AND GRILL', to: '/bar-and-grill' },
    { label: 'TOURS', to: '/tours' },
    { label: 'CONTACT US', to: '/contact' },
  ];

  return (
    <footer ref={footerRef} className="w-full" style={{ backgroundColor: '#000000' }}>
      {/* Contact Info Section */}
      <div className="pt-8 md:pt-10 pb-2 md:pb-3">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-12">
            {/* Left Column - Social Icons, Logo, Brand Name, Contact Info */}
            <div 
              className="space-y-6 transition-all duration-700 ease-out"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible 
                  ? 'perspective(1000px) translateZ(0) scale(1)' 
                  : 'perspective(1000px) translateZ(-200px) scale(0.7)'
              }}
            >
              {/* Social Icons */}
              <div className="flex items-center space-x-3 mt-8 md:mt-12">
                <a 
                  href="https://instagram.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-amber-100 hover:bg-amber-200 transition-colors rounded"
                  style={{ backgroundColor: '#d4a574' }}
                >
                  <Instagram size={20} className="text-gray-800" />
                </a>
                <a 
                  href="https://facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-amber-100 hover:bg-amber-200 transition-colors rounded"
                  style={{ backgroundColor: '#d4a574' }}
                >
                  <Facebook size={20} className="text-gray-800" />
                </a>
                <a 
                  href="https://x.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 bg-amber-100 hover:bg-amber-200 transition-colors rounded"
                  style={{ backgroundColor: '#d4a574' }}
                >
                  <Twitter size={20} className="text-gray-800" />
                </a>
              </div>

              {/* Brand Name */}
              <h3 className="text-white font-bold text-2xl tracking-wide">IKORO HOUSE</h3>

              {/* Contact Information */}
              <div className="space-y-2 text-white text-base">
                <a 
                  href="https://maps.app.goo.gl/x1HdLHNZZ9zJGsJPA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:text-amber-200 transition-colors"
                >
                  <p>KK 587A St, Rebero</p>
                  <p>Kigali, Rwanda</p>
                </a>
                <a href="tel:+250791874212" className="block pt-2 hover:text-amber-200 transition-colors">
                  +250 - 791 - 874 212
                </a>
                <p>info@ikorohouse.com</p>
              </div>
            </div>

            {/* Right Column - Large Logo - Aligned with LOGIN button */}
            <div 
              className="flex justify-center md:justify-end items-center md:items-start md:ml-auto transition-all duration-700 ease-out"
              style={{
                transitionDelay: '200ms',
                opacity: isVisible ? 1 : 0,
                transform: isVisible 
                  ? 'perspective(1000px) translateZ(0) scale(1)' 
                  : 'perspective(1000px) translateZ(-200px) scale(0.7)'
              }}
            >
              <img 
                src="/brand.svg" 
                alt="Ikoro House" 
                className="h-48 w-48 md:h-56 md:w-56 lg:h-64 lg:w-64 object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Links */}
      <div className="pt-2 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="flex flex-wrap justify-center gap-6 md:gap-8 lg:gap-12 transition-all duration-700 ease-out"
            style={{
              transitionDelay: '300ms',
              opacity: isVisible ? 1 : 0,
              transform: isVisible 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-200px) scale(0.7)'
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-white hover:text-amber-200 transition-colors font-semibold text-sm lg:text-base uppercase tracking-wider"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Managed by UrutiHub */}
      <div className="pt-4 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p 
            className="text-white text-center text-sm mt-4 transition-all duration-700 ease-out"
            style={{
              transitionDelay: '400ms',
              opacity: isVisible ? 1 : 0,
              transform: isVisible 
                ? 'perspective(1000px) translateZ(0) scale(1)' 
                : 'perspective(1000px) translateZ(-200px) scale(0.7)'
            }}
          >
            Managed by UrutiHub
          </p>
        </div>
      </div>
    </footer>
  );
}